/**
 * hasFeature - Feature gating system for CodeDesign v2
 * Port of nocode-frontend's hasFeature logic adapted for Zustand/React Query
 * 
 * Usage:
 *   import { hasFeature } from '@/lib/hasFeature'
 *   hasFeature('DOMAIN_CONNECTION', () => { doTheAction() }, () => { console.log('blocked') })
 */

import { useUIStore } from '@/state/ui.store'
import { useSelectionStore } from '@/state/selection.store'
import { showSimpleError } from '@/lib/toast'
import { safeLocalStorage } from '@/lib/safe-storage'

// Plan constants
const PLAN_BASIC = 'basic'
const PLAN_PREMIUM = 'premium'
const PLAN_STANDARD = 'standard'

/**
 * Gets the current state from stores (non-React context)
 * For use in the hasFeature function which runs outside React
 */
function getState() {
    const selectionState = useSelectionStore.getState()

    // Get user data from localStorage (since we don't have Redux)
    let userData = {}
    try {
        const stored = safeLocalStorage.getItem('user_data')
        if (stored) userData = JSON.parse(stored)
    } catch (e) {
        console.error('[hasFeature] Error parsing user_data:', e)
    }

    // Get workspace from selection store
    const workspace = selectionState.currentWorkspace || {}
    const project = selectionState.currentProject || {}
    const projectOrder = project?.order || {}

    return {
        user: userData,
        workspace,
        project,
        projectOrder,
        isLoggedIn: !!userData?.id,
        isAdmin: userData?.isAdmin || false,
        userPlan: userData?.order || {},
        // Use activePlans from API if available, otherwise derive from plans
        activePlans: workspace?.activePlans || (workspace?.plans || []).filter(p => p?.isActive),
        licenses: workspace?.licenses || {},
        paidProjects: workspace?.paidProjects || {},
        playground: project?.playground || false,
    }
}

// Flag to control whether AI Companion check is required for workspace-based plans
// Set to false to always check activePlans regardless of hasAICompanionEnabled
const USE_AI_COMPANION_FLAG = false

/**
 * Helper to check if user has pro access (either regular paid or workspace with active plans)
 */
function isUserProAccess(state) {
    // Check workspace-based plans (activePlans)
    if (USE_AI_COMPANION_FLAG) {
        // Original behavior: only check activePlans if user has AI Companion enabled
        if (state.user?.hasAICompanionEnabled) {
            return (state.activePlans || []).length > 0
        }
    } else {
        // New behavior: always check activePlans first
        if ((state.activePlans || []).length > 0) {
            return true
        }
    }
    // Legacy: check user plan
    return Boolean(state.userPlan?.paid)
}

/**
 * Helper to check if project OR user has pro access
 */
function isProjectOrUserProAccess(state) {
    return state.projectOrder?.paid || isUserProAccess(state)
}

/**
 * Get the user type based on their plan
 */
function getUserType(state) {
    const plan = state.userPlan?.plan?.toLowerCase() || ''

    if (state.isAdmin) return 'admin'
    if (!state.isLoggedIn) return 'guest'
    if (plan === PLAN_BASIC) return 'basic'
    if (plan === PLAN_PREMIUM) return 'premium'
    if (plan === PLAN_STANDARD) return 'advanced'
    return 'free'
}

/**
 * Check if it's a CodeDesign team member
 */
function isCodeDesignTeam(state) {
    return state.user?.email?.endsWith('codedesign.app') || state.isAdmin
}

/**
 * Show the pricing modal
 */
function showPricingModal(caseItem = '', callback = null) {
    useUIStore.getState().setShowPricingModal(true, caseItem, callback)
}

/**
 * Show the guest login modal
 */
function showGuestModal() {
    useUIStore.getState().setShowGuestModal(true)
}

/**
 * Show select edit mode modal (for playground)
 */
function showSelectEditModeModal() {
    useUIStore.getState().setShowSelectEditModeModal(true)
}

/**
 * Handle error - show appropriate modal based on user type
 */
function handleError(state, userType, errorCallback, options = {}) {
    const { caseItem = '' } = options

    // Check if errorCallback is an array of limit types
    const errorTypes = Array.isArray(errorCallback) ? errorCallback : []

    if (userType === 'guest' && errorTypes.includes('limitGuest')) {
        showGuestModal()
    } else if (state.playground && errorTypes.includes('limitPlayground')) {
        showSelectEditModeModal()
    } else if (!isProjectOrUserProAccess(state) && errorTypes.includes('limitPremium')) {
        showPricingModal(caseItem)

        // Additional check for LTD/Agency/Growth
        if (errorTypes.includes('limitLTD')) {
            const projectPlan = state.projectOrder?.plan || ''
            const userPlan = state.userPlan?.plan || ''
            const plan = projectPlan || userPlan
            const isLTD = plan.includes('LTD')
            const isAgency = plan.toLowerCase().includes('agency')
            const isGrowth = plan.toLowerCase().includes('growth')

            if (!isLTD && !isAgency && !isGrowth) {
                showSimpleError('This feature is only available for Lifetime deal, Agency, or Growth plan users')
            }
        }
    } else if (typeof errorCallback === 'function') {
        errorCallback()
    } else {
        // Default: show pricing modal when no specific handler
        showPricingModal(caseItem)
    }
}

/**
 * Main hasFeature function
 * @param {string} featureName - The feature to check
 * @param {function} callback - Called if feature is allowed
 * @param {function|array} errorCallback - Called if feature is blocked, or array of limit types
 */
export function hasFeature(featureName, callback, errorCallback) {
    const state = getState()
    const userType = getUserType(state)

    // Default pricing-modal attribution to the gated feature so every trigger is
    // identifiable; an explicit caseItem (e.g. 'REGENERATE_SITE') still wins.
    const error = (options = {}) => handleError(state, userType, errorCallback, { caseItem: featureName, ...options })

    switch (featureName) {
        case 'SAVE_AI_PLAYGROUND_PROJECT': {
            if (isUserProAccess(state)) {
                return callback()
            } else {
                return error()
            }
        }

        case 'PREMIUM_ELEMENT_DROPPING': {
            if (isProjectOrUserProAccess(state)) {
                return callback()
            } else {
                return error()
            }
        }

        case 'GENERIC_ACTION_ALLOW_ONLY_USER_PLAN_PAID': {
            if (isUserProAccess(state)) {
                return callback()
            } else {
                return error()
            }
        }

        case 'ADD_PROJECT': {
            return callback()
        }

        case 'DEPLOY_PROJECT': {
            if (userType === 'guest') return error()
            else return callback()
        }

        case 'GENERIC_ACTION': {
            // Limit guest, limit playground
            if (userType === 'guest' || state.playground) return error()
            else return callback()
        }

        case 'DATABASE_AND_SETTINGS': {
            if (state.playground) return error()
            else return callback()
        }

        case 'DOMAIN_CONNECTION': {
            // If the project itself already has a license, allow
            if (state.projectOrder?.paid) {
                return callback()
            }
            // Workspace has pro access but project isn't licensed — show pricing modal
            // so the user can assign a license from their workspace plan
            if (isUserProAccess(state)) {
                return showPricingModal('DOMAIN_CONNECTION', callback)
            }
            return error()
        }

        case 'HIDE_BRANDING': {
            if (isProjectOrUserProAccess(state)) {
                return callback()
            } else {
                return error()
            }
        }

        case 'GENERIC_ACTION_ALLOW_ONLY_PAID': {
            if (isProjectOrUserProAccess(state)) {
                return callback()
            } else {
                return error()
            }
        }

        case 'GENERIC_ACTION_ALLOW_ONLY_PAID_OR_CODEDESIGN_MEMBERS': {
            if (isProjectOrUserProAccess(state) || isCodeDesignTeam(state)) {
                return callback()
            } else {
                return error()
            }
        }

        case 'DB_ACCESS': {
            if (getUserType(state) !== 'free') {
                return callback()
            } else {
                return error()
            }
        }

        case 'WORKFLOW_ACCESS': {
            if (getUserType(state) !== 'free') {
                return callback()
            } else {
                return error()
            }
        }

        case 'REACT_EXPORT': {
            if (getUserType(state) !== 'free') {
                return callback()
            } else {
                return error()
            }
        }

        case 'WHOLE_PROJECT_EXPORT': {
            if (
                getUserType(state) !== 'free' &&
                state.projectOrder?.plan?.includes('LTD')
            ) {
                return callback()
            } else {
                return error()
            }
        }

        case 'SINGLE_PAGE_EXPORT': {
            const projectPlan = state.projectOrder?.plan || ''
            const userPlan = state.userPlan?.plan || ''
            let plan = projectPlan || userPlan

            // Check workspace plans for AI Companion users
            if (state.user?.hasAICompanionEnabled && state.activePlans?.length > 0) {
                const workspacePlan = state.activePlans[0]
                plan = plan || workspacePlan?.planName || ''
            }

            const isLTD = plan.includes('LTD') || plan.toLowerCase().includes('lifetime')
            const isAgency = plan.toLowerCase().includes('agency')
            const isGrowth = plan.toLowerCase().includes('growth')
            const isPaid = isProjectOrUserProAccess(state)

            if (isPaid && (isLTD || isAgency || isGrowth)) {
                return callback()
            } else {
                return error()
            }
        }

        case 'REQUEST_EARLY_ACCESS_BUTTON': {
            if (isProjectOrUserProAccess(state)) {
                return callback()
            } else {
                return error()
            }
        }

        case 'REGENERATE_SITE': {
            if (isUserProAccess(state) || isCodeDesignTeam(state)) {
                return callback()
            } else {
                return error({ caseItem: 'REGENERATE_SITE' })
            }
        }

        case 'CREATE_DATABASE': {
            if (isProjectOrUserProAccess(state)) {
                return callback()
            } else {
                return error()
            }
        }

        case 'GENERIC_ACTION_PLAYGROUND_LIMIT': {
            if (userType !== 'guest' && !state.playground) {
                return callback()
            } else {
                return error()
            }
        }

        case 'ADD_PAGE': {
            if (userType !== 'guest' && !state.playground) {
                // Check 3-page limit for free users
                const pageCount = Object.keys(state.project?.blueprint || {})?.length || 0
                if (pageCount >= 3 && !isProjectOrUserProAccess(state)) {
                    return error()
                } else {
                    return callback()
                }
            } else {
                return error()
            }
        }

        case 'ADD_TEAM_MEMBER': {
            if (isUserProAccess(state)) {
                return callback()
            } else {
                return error({ caseItem: 'ADD_TEAM_MEMBER' })
            }
        }

        case 'TRANSFER_PROJECT': {
            if (isUserProAccess(state)) {
                return callback()
            } else {
                return error({ caseItem: 'TRANSFER_PROJECT' })
            }
        }

        case 'TRANSFER_WORKSPACE': {
            if (isUserProAccess(state)) {
                return callback()
            } else {
                return error({ caseItem: 'TRANSFER_WORKSPACE' })
            }
        }

        case 'PAID_TEMPLATE_ACTION': {
            // Copy prompt / Export code / Open in builder for paid-tier templates.
            // Free templates skip this check entirely (the caller decides).
            if (isUserProAccess(state) || isCodeDesignTeam(state)) {
                return callback()
            } else {
                return error({ caseItem: 'PAID_TEMPLATE_ACTION' })
            }
        }

        default:
            console.warn(`[hasFeature] Unknown feature: ${featureName}`)
            return ''
    }
}

export default hasFeature
